/**
 * DeepFilterNet3 Processor — state-of-the-art AI noise suppression.
 *
 * Phase 2.C of the denoise эталон: WASM frame loop now runs in a Module
 * Worker (deepfilter-worker.js) so the main thread stays responsive
 * during multi-pass max-strength runs (was ~120 s of UI freeze prior;
 * now <16 ms blocked between resample + gain-transfer math).
 *
 * Public API matches the prior main-thread version (and matches
 * RNNoiseProcessor for drop-in replacement):
 *   init(onProgress)               → Promise<boolean>
 *   isLoaded()                     → boolean
 *   setAttenLimit(limDb)           → void  (queues if not loaded yet)
 *   setPostFilterBeta(beta)        → void
 *   process(audioBuffer, strength, onProgress) → { channels, sampleRate, numberOfChannels }
 *   reinit()                       → Promise<boolean>
 *
 * Architecture split:
 *   • main thread: AudioContext resample to 48 kHz · mono mix-down ·
 *     stereo gain-transfer reconstruction · wet/dry mix · onProgress
 *     fanout · pending-promise bookkeeping
 *   • worker:      df.js import · WASM init · model fetch + state ·
 *     df_set_atten_lim / df_set_post_filter_beta · frame-by-frame
 *     df_process_frame loop
 *
 * Float32Array crosses postMessage as Transferable (zero-copy ownership
 * transfer). The repo bans cross-origin isolation / SAB (RU traffic
 * regression 2024), so this is the right shape for our workload.
 */
window.DeepFilterProcessor = (function() {
    /* ⚠️ Через `__jsSrc`, а не голым путём — тот же приём, что у mp3-encoder,
       audio-background, auto-captions, microscope и ai-upscale.
       Замер 2026-09-01, сразу после выкладки правки В САМ ВОРКЕР: RU отдавал
       свежие 12 904 байта, а intl — 11 103 байта от 22 ИЮЛЯ, `immutable`,
       `max-age=15552000`, `cf-cache-status: HIT`. То есть на intl жил воркер
       СОРОКАОДНОДНЕВНОЙ давности и прожил бы ещё полгода, пока деплой
       рапортует успех: голый `/js/…` не проходит через версионирование, а
       статика на intl отдаётся неизменяемой. Правка в воркере физически не
       доезжала до этих людей.
       С версией в URL возврат посетителя даёт промах кэша и свежий воркер —
       чинится само, без выкорчёвывания кэша Cloudflare.
       Фолбэк на голый путь оставлен намеренно: если `__jsSrc` почему-то нет,
       воркер обязан подняться, пусть и из кэша. */
    var WORKER_URL = (typeof window !== 'undefined' && window.__jsSrc)
        ? window.__jsSrc('/js/deepfilter-worker.js')
        : '/js/deepfilter-worker.js';
    // Default to RU-origin paths. Tools should override via init({urls, urlsFallback})
    // computed from server-side AssetRouter::heavy() / heavyFallback() so non-RU
    // users hit the HuggingFace mirror first and fall back to RU origin if HF
    // returns 5xx or network error. Per-file URL map (not basePath) is required
    // because HF resolve URLs encode owner/repo/file with no shared directory prefix.
    var BASE_PATH = '/vendor/deepfilter/';
    var BASE_PATH_FALLBACK = null;
    var URLS = null;          // { 'df.js': 'https://...', 'df_bg.wasm': '...', 'deepfilter3_model.tar.gz': '...' }
    var URLS_FALLBACK = null; // same shape; missing keys fall through to basePath + filename
    var SAMPLE_RATE = 48000;
    var FRAME_SIZE = 480;

    var _worker = null;
    var _loaded = false;
    var _initPromise = null;
    var _pending = new Map();      // id → { resolve, reject, onProgress }
    var _nextId = 1;
    var _stageGates = null;         // 1 = the worker opened the stage gates, 0 = old library, null = not loaded yet
    var _lastError = null;          // last init error — read by EngineWarmer wrappers to enrich `engine_warm_failed` payload
    // Configuration is queued when called before init resolves; flushed via
    // _flushQueuedConfig once the worker confirms ready.
    var _queuedAtten = null;
    var _queuedBeta = null;

    // Heartbeat watchdog (2026-05-07) — same protection as mp3-encoder.
    // DeepFilterNet's df_process_frame loop emits a progress event per
    // ~10 ms of audio, so even on slow Android the gap stays well under
    // a second. 30 s of silence = the OS killed the worker (ONNX model
    // memory pressure is a known trigger on 4 GB devices).
    //
    // DEPENDENCY IS SELF-LOADED (fix 2026-07-11, comment #969 Анатолий,
    // sid 5aadbcab): the original comment here said "module loads in
    // parallel with this file via ensureAudioPipeline" — TRUE only for the
    // pitch/mp3-encoder tool family. The six blades that load
    // deepfilter-processor directly (denoise, audio-enhancer, auto-captions,
    // edit-video-sound, speech-restorer, voice-enhancer) never call
    // ensureAudioPipeline, so window.WorkerWatchdog was undefined there and
    // the feature-detect below SILENTLY disabled the protection — exactly
    // the case the watchdog exists for: Android OOM-kills the DFN3 worker,
    // postMessage drops silently, the process() promise hangs forever, the
    // user stares at a frozen progress bar («постоянно зависает в одном
    // месте», 2×wasm_stall 93s/720s, zero tool_error). Self-loading the
    // 5.5 KB script here guarantees every consumer gets the protection —
    // the 7th future blade can't forget a tag it never had to write.
    var _wd = null;
    var _wdLoadTried = false;
    function _ensureWatchdogScript(){
        if (window.WorkerWatchdog || _wdLoadTried || typeof document === 'undefined') return;
        _wdLoadTried = true;
        try {
            var s = document.createElement('script');
            s.src = '/js/worker-watchdog.js';
            s.async = true;
            // Create the instance the moment the script lands — do NOT wait
            // for the next _send/_onWorkerMessage to call _watchdog(). If
            // the worker dies while the script is still in flight there may
            // BE no next message, and the lazily-created watchdog would
            // never exist → the hang it guards against. onload → create →
            // retroactive mark of all pending ids → poll catches the corpse
            // one timeout later, no matter how dead the worker is.
            s.onload = function(){ try { _watchdog(); } catch(_){} };
            document.head.appendChild(s);
        } catch(_){}
    }
    function _watchdog(){
        if (_wd) return _wd;
        if (!window.WorkerWatchdog) { _ensureWatchdogScript(); return null; }
        _wd = _createWatchdog();
        // Retroactive mark — closes the self-load race: when the worker is
        // created on the CLICK (no upload-time warm), the first process
        // _send can run before the 5.5 KB script lands; that job would
        // never be marked and touch() ignores unknown ids, so the hang
        // protection would silently skip exactly the job that matters
        // (bit on prod verify: worktree won the race via upload-time warm,
        // prod lost it). Marking every currently-pending id on creation
        // makes the baseline "now" — detection still fires within one
        // timeout of the script landing.
        try { _pending.forEach(function(_p, id){ _wd.mark(id); }); } catch(_){}
        return _wd;
    }
    function _createWatchdog(){
        return window.WorkerWatchdog.create({
            name: 'deepfilter',
            getPending: function(){ return _pending; },
            terminateWorker: function(){
                try { if (_worker) _worker.terminate(); } catch(_){}
                _worker = null;
                _loaded = false;
                _initPromise = null;
            },
            getHeartbeatTimeoutMs: function(){
                return Number(window.__DEEPFILTER_WORKER_TIMEOUT_OVERRIDE) || 30000;
            },
            getTickMs: function(){
                return Number(window.__DEEPFILTER_WATCHDOG_TICK_OVERRIDE) || 5000;
            },
            /* Ask before convicting. This worker is silent for the whole
               8.97 MB wasm download and the df.js import — both stretches
               where its message loop is free and it would answer instantly.
               Without this the watchdog killed 103 healthy warm-ups across 98
               sessions in 7 days (denoise / audio-enhancer / voice-enhancer /
               speech-restorer), and the tool silently fell back to the weaker
               rnnoise engine. See worker-watchdog.js for the contract. */
            pingWorker: function(id){
                try { if (_worker) _worker.postMessage({ id: id, type: '__wdPing' }); } catch(_){}
            },
        });
    }

    function _ensureWorker(){
        if (_worker) return _worker;
        // An engine without module workers (Firefox < 114, Safari < 15) starts this
        // worker as a classic script, where its import() of the model runtime is
        // refused: init() came back false only after the worker's own retries, and
        // each tool then fell back (RNNoise, the FFmpeg filter). Asked first, init()
        // is false at once — the same fallback, no worker, no wait.
        if (typeof window !== 'undefined' && window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
            var tooOld = new Error('module_worker_unsupported');
            tooOld.code = 'MODULE_WORKER_UNSUPPORTED';
            throw tooOld;
        }
        // Kick the watchdog-script load alongside worker creation so the
        // protection is in place before the first process job (init — the
        // model download — takes far longer than the 5.5 KB script).
        _ensureWatchdogScript();
        _worker = new Worker(WORKER_URL, { type: 'module' });
        _worker.addEventListener('message', _onWorkerMessage);
        _worker.addEventListener('error', function(e){
            // Reject every pending promise — main thread cannot recover from
            // a worker-level error (script load fail, parse fail, etc.). The
            // failure is handed to the caller (init() → false, process() rejects,
            // and every tool has its fallback), so it is cancelled here: uncancelled
            // it would also reach window.onerror — the shared toast, Sentry.
            if (e && e.preventDefault) e.preventDefault();
            try { if (typeof window !== 'undefined' && window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'dfn_worker_error', msg: String((e && e.message) || '').slice(0, 120) }); } catch(_){}
            var msg = (e && e.message) || 'worker error';
            _pending.forEach(function(p){ try { p.reject(new Error(msg)); } catch(_){} });
            _pending.clear();
            var wd = _watchdog(); if (wd) wd.dispose();
            try { _worker.terminate(); } catch(_){}
            _worker = null;
            _loaded = false;
            _initPromise = null;
        });
        return _worker;
    }

    function _onWorkerMessage(ev){
        var m = ev.data || {};
        var p = _pending.get(m.id);
        if (!p) return;
        // Any message from the worker is a heartbeat — touch the watchdog.
        var wd = _watchdog(); if (wd) wd.touch(m.id);
        switch (m.type) {
            case 'progress':
                if (p.onProgress) try { p.onProgress(m.ratio || 0, m); } catch(_){}
                break;
            case 'ready':
                if (m.frameSize > 0) FRAME_SIZE = m.frameSize;
                // Our build sets the runtime's stage gates (deepfilter-worker.js,
                // applyStageGates). gates:0 = the page got a library without the
                // setter — a cached page with the old URL map — and runs the old,
                // gated network. Said once per page so the share can be counted.
                if (typeof m.gates === 'number') {
                    if (_stageGates === null && m.gates === 0) {
                        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'dfn_no_stage_gates' }); } catch(_){}
                    }
                    _stageGates = m.gates;
                }
                _loaded = true;
                _pending.delete(m.id);
                if (wd) wd.forget(m.id);
                p.resolve(true);
                _flushQueuedConfig();
                break;
            case 'configured':
                _pending.delete(m.id);
                if (wd) wd.forget(m.id);
                p.resolve(true);
                break;
            case 'result':
                _pending.delete(m.id);
                if (wd) wd.forget(m.id);
                p.resolve(m.mono);
                break;
            case 'error':
                _pending.delete(m.id);
                if (wd) wd.forget(m.id);
                p.reject(new Error(m.message || 'worker error'));
                break;
        }
    }

    function _send(payload, onProgress, transferList){
        var id = _nextId++;
        return new Promise(function(resolve, reject){
            _pending.set(id, { resolve: resolve, reject: reject, onProgress: onProgress });
            var wd = _watchdog(); if (wd) wd.mark(id);
            payload.id = id;
            try {
                _ensureWorker().postMessage(payload, transferList || []);
            } catch (e) {
                _pending.delete(id);
                if (wd) wd.forget(id);
                reject(e);
            }
        });
    }

    function _flushQueuedConfig(){
        if (!_loaded) return;
        if (_queuedAtten !== null || _queuedBeta !== null) {
            _send({ type: 'configure', attenLimit: _queuedAtten, postFilterBeta: _queuedBeta })
                .catch(function(){ /* configure failure is non-fatal — atten just stays at default 100 */ });
            _queuedAtten = null;
            _queuedBeta = null;
        }
    }

    var _nextStreamKey = 1;

    function _cat(a, b){
        var r = new Float32Array(a.length + b.length);
        r.set(a); r.set(b, a.length);
        return r;
    }

    // Resample to 48 kHz on the main thread (OfflineAudioContext is fast).
    async function _to48k(audioBuffer){
        if (audioBuffer.sampleRate === SAMPLE_RATE) return audioBuffer;
        var channels = audioBuffer.numberOfChannels;
        var newLength = Math.ceil(audioBuffer.duration * SAMPLE_RATE);
        var offlineCtx = new OfflineAudioContext(channels, newLength, SAMPLE_RATE);
        var source = offlineCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineCtx.destination);
        source.start();
        return offlineCtx.startRendering();
    }

    // Mono mix-down (a fresh array: it is transferred to the worker).
    function _monoOf(chs){
        var n = chs[0].length, mono = new Float32Array(n);
        if (chs.length === 1) { mono.set(chs[0]); return mono; }
        var a = chs[0], b = chs[1];
        for (var i = 0; i < n; i++) mono[i] = (a[i] + b[i]) * 0.5;
        return mono;
    }

    // Wet/dry blend for mono. Stereo is mid/side: the model cleans the mid
    // (L+R)/2 exactly as it cleans a mono file, and only the side (L−R)/2
    // gets the model's per-frame gain — the image survives a mono model and
    // the voice, which sits in the mid, is denoised in frequency, not gated.
    // Until 2026-09-24 BOTH channels got only that per-frame gain: noise
    // under the voice stayed wherever the voice was. 81 % of /denoise inputs
    // are stereo (RU 30 d), 30 % of them exact dual-mono; measured on stereo
    // speech after DFN3 at the page's mix: dual-mono DNSMOS OVRL 2.89 → 3.03,
    // STOI 0.909 → 0.942 (the gate had lowered it below the input's 0.925);
    // two-mic phone stereo 2.88 → 2.96, 0.923 → 0.934 — at the same cost (one
    // model pass; per-channel passes scored the same OVRL at twice the time).
    // wet, dry and monoDry are aligned and equally long.
    function _blend(wet, dryCh, monoDry, strength){
        var totalSamples = wet.length;
        if (dryCh.length === 1) {
            var output = new Float32Array(totalSamples);
            var dry = dryCh[0];
            for (var k = 0; k < totalSamples; k++) {
                output[k] = wet[k] * strength + dry[k] * (1 - strength);
            }
            return { channels: [output], gains: null };
        }
        var ch0 = dryCh[0], ch1 = dryCh[1];
        var outL = new Float32Array(totalSamples);
        var outR = new Float32Array(totalSamples);
        var GAIN_FRAME = FRAME_SIZE;
        var gainFrames = Math.ceil(totalSamples / GAIN_FRAME);
        var gains = new Float32Array(gainFrames);
        var EPS = 1e-10;
        for (var gf = 0; gf < gainFrames; gf++) {
            var gOff = gf * GAIN_FRAME;
            var gEnd = Math.min(gOff + GAIN_FRAME, totalSamples);
            var rmsIn = 0, rmsOut = 0;
            for (var gi = gOff; gi < gEnd; gi++) {
                rmsIn += monoDry[gi] * monoDry[gi];
                rmsOut += wet[gi] * wet[gi];
            }
            var len = gEnd - gOff;
            rmsIn = Math.sqrt(rmsIn / len);
            rmsOut = Math.sqrt(rmsOut / len);
            gains[gf] = rmsIn > EPS ? Math.min(rmsOut / rmsIn, 1.5) : 1.0;
        }
        var smoothed = new Float32Array(gainFrames);
        for (var gs = 0; gs < gainFrames; gs++) {
            var prev = gs > 0 ? gains[gs - 1] : gains[gs];
            var next = gs < gainFrames - 1 ? gains[gs + 1] : gains[gs];
            smoothed[gs] = prev * 0.25 + gains[gs] * 0.5 + next * 0.25;
        }
        for (var ii = 0; ii < totalSamples; ii++) {
            var framePos = ii / GAIN_FRAME;
            var frame0 = Math.floor(framePos);
            var frame1 = Math.min(frame0 + 1, gainFrames - 1);
            var frac = framePos - frame0;
            var gain = smoothed[frame0] * (1 - frac) + smoothed[frame1] * frac;
            var effectiveGain = 1.0 + (gain - 1.0) * strength;
            var mid = wet[ii] * strength + monoDry[ii] * (1 - strength);
            var side = (ch0[ii] - ch1[ii]) * 0.5 * effectiveGain;
            outL[ii] = mid + side;
            outR[ii] = mid - side;
        }
        return { channels: [outL, outR], gains: smoothed };
    }

    return {
        /**
         * Initialize the worker, load WASM + model, create the DFN3 state.
         * Idempotent — second call reuses the pending promise (or returns
         * `true` immediately when already loaded).
         */
        init: async function(onProgressOrOpts, maybeOnProgress){
            // Back-compat: init(onProgress) — function arg → progress callback only.
            // New shape:   init({urls, urlsFallback, basePath, basePathFallback, onProgress}).
            //              init({urls, urlsFallback, ...}, onProgress).
            // Both shapes coexist so callers can migrate at their own pace.
            var onProgress = null;
            var opts = null;
            if (typeof onProgressOrOpts === 'function') {
                onProgress = onProgressOrOpts;
            } else if (onProgressOrOpts && typeof onProgressOrOpts === 'object') {
                opts = onProgressOrOpts;
                onProgress = (typeof maybeOnProgress === 'function')
                    ? maybeOnProgress
                    : (typeof opts.onProgress === 'function' ? opts.onProgress : null);
            }
            // First explicit opts win; subsequent calls with different paths are
            // ignored (we already loaded model from the first source — switching
            // mid-session would require reinit).
            if (opts && !_loaded && !_initPromise) {
                if (typeof opts.basePath === 'string') {
                    BASE_PATH = opts.basePath;
                    BASE_PATH_FALLBACK = (typeof opts.basePathFallback === 'string') ? opts.basePathFallback : null;
                }
                if (opts.urls && typeof opts.urls === 'object') URLS = opts.urls;
                if (opts.urlsFallback && typeof opts.urlsFallback === 'object') URLS_FALLBACK = opts.urlsFallback;
            }
            if (_loaded) { if (onProgress) onProgress(1); return true; }
            if (_initPromise) {
                // Late-attached progress listeners get the in-flight stream.
                // Without this two callers (eg. EngineWarmer.warm + a user
                // click) racing init both want progress updates; we fan out.
                if (onProgress) {
                    var prior = _initPromise.__onProgressCb;
                    _initPromise.__onProgressCb = function(r, m){
                        try { prior && prior(r, m); } catch(_){}
                        try { onProgress(r, m); } catch(_){}
                    };
                }
                return _initPromise;
            }
            _lastError = null;
            // The progress callback is reassigned (above) when more callers
            // attach mid-flight; route through the holder so all listeners fire.
            var holder = { fn: onProgress };
            _initPromise = _send({ type: 'init', basePath: BASE_PATH, basePathFallback: BASE_PATH_FALLBACK, urls: URLS, urlsFallback: URLS_FALLBACK }, function(r, m){
                try { holder.fn && holder.fn(r, m); } catch(_){}
            }).then(function(ok){
                _initPromise = null;
                return !!ok;
            }, function(err){
                _initPromise = null;
                _loaded = false;
                _lastError = err;
                console.error('DeepFilterNet init failed:', err);
                return false;
            });
            _initPromise.__onProgressCb = holder.fn;
            // Replace holder.fn with a getter that always reads the latest
            // __onProgressCb so reassignments above flow through.
            Object.defineProperty(holder, 'fn', {
                get: function(){ return _initPromise && _initPromise.__onProgressCb; }
            });
            return _initPromise;
        },

        isLoaded: function(){ return _loaded; },

        setAttenLimit: function(limDb){
            if (!_loaded) { _queuedAtten = limDb; return; }
            _send({ type: 'configure', attenLimit: limDb }).catch(function(){});
        },

        setPostFilterBeta: function(beta){
            if (!_loaded) { _queuedBeta = beta; return; }
            _send({ type: 'configure', postFilterBeta: beta }).catch(function(){});
        },

        /**
         * Process audioBuffer through DeepFilterNet3 in the worker.
         * Returns the same shape the prior main-thread impl returned so the
         * caller (denoise blade) doesn't change. The wet signal comes back
         * sample-aligned with the input (the worker compensates the model's
         * 30 ms delay — see DELAY_FRAMES there), so the wet/dry blend below is
         * a blend, not a comb filter.
         */
        /* opts (additive, 2026-07-16 — voice-enhancer strength slider):
         *   { wantRaw: true } → the result additionally carries
         *   raw: { monoWet:   Float32Array — the worker's fully-wet mono
         *                     (pre-blend; UNSCALED by `strength`),
         *          gains:     Float32Array|null — SMOOTHED stereo gain envelope
         *                     (one value per FRAME_SIZE=480 samples ≈10 ms;
         *                     null for mono input),
         *          gainFrame: FRAME_SIZE }
         *   The caller can then re-blend ANY strength k without re-inference:
         *   mono   out = monoWet·k + dry·(1−k)          (dry = 48k channel)
         *   stereo out = ch·(1 + (gain−1)·k)            (per-sample, gains
         *                indexed by time — sample-rate independent). */
        process: async function(audioBuffer, strength, onProgress, opts){
            if (!_loaded) throw new Error('DeepFilterNet not initialized');
            var wantRaw = !!(opts && opts.wantRaw);

            var channels = audioBuffer.numberOfChannels;

            // Step 1: resample to 48 kHz on main thread (cheap; OfflineAudioContext
            // is hardware-fast). Keeps the worker focused on WASM frames only.
            if (onProgress) onProgress(0.05);
            var buffer48k = await _to48k(audioBuffer);
            if (onProgress) onProgress(0.1);
            var dryCh = [];
            for (var c = 0; c < channels; c++) dryCh.push(buffer48k.getChannelData(c));

            // Step 2: mono mix on main thread. DFN3 has a single internal
            // state; running stereo channels sequentially through it
            // corrupts channel 2's output. Mono mix → process → gain-
            // transfer back to stereo (Step 4) preserves stereo imaging.
            var monoData = _monoOf(dryCh);

            // Step 3: ship the mono buffer to the worker, await processed
            // mono back. Transferable transfer → no copy. Worker streams
            // ratio progress every BATCH_SIZE frames.
            //
            // CAREFUL: monoData.buffer ownership transfers to the worker
            // here; we must NOT read monoData after this line. We need a
            // copy for the gain-transfer math below — make it BEFORE the
            // transfer.
            var monoForGain = (channels > 1) ? new Float32Array(monoData) : null;
            var monoProcessed = await _send(
                { type: 'process', mono: monoData },
                function(ratio){
                    if (onProgress) {
                        // ratio in [0,1] from worker; map into [0.1, 0.85]
                        // of the overall progress bar, leaving 0.85+ for
                        // the gain-transfer math.
                        onProgress(0.1 + ratio * 0.75);
                    }
                },
                [monoData.buffer]
            );

            if (onProgress) onProgress(0.85);

            // Step 4: wet/dry blend (mono) or gain-transfer stereo
            // reconstruction (main-thread, no WASM).
            var mixed = _blend(monoProcessed, dryCh, monoForGain, strength);

            if (onProgress) onProgress(1);
            var result = {
                channels: mixed.channels,
                sampleRate: SAMPLE_RATE,
                numberOfChannels: channels
            };
            if (wantRaw) {
                result.raw = {
                    monoWet: monoProcessed,
                    gains: mixed.gains,
                    gainFrame: FRAME_SIZE,
                    // Mono re-blends happen in the 48k domain — hand the caller
                    // the resampled dry reference (zero-copy when the source is
                    // already 48k). Stereo re-blends use the source's native
                    // channels + the gain envelope, no dry needed.
                    dry48: (channels === 1) ? dryCh[0] : null
                };
            }
            return result;
        },

        /**
         * ONE recording fed in pieces (/denoise's chunked paths: 30-s
         * segments of a long file). Separate process() calls would flush the
         * model after every piece and start the next one from an empty
         * overlap-add — measured 2026-09-24: a dropout to silence at a seam
         * that fell inside a word (envelope −166 dB, a 0.13 step). A stream
         * keeps the model running across pieces; it drops the model delay
         * once at the start and flushes it once in finish().
         *
         *   var st = DeepFilterProcessor.openStream();
         *   out = await st.process(piece, strength, onProgress)  // per piece
         *   out = await st.finish(strength)                       // the rest
         *
         * Each call returns the samples that are FINISHED, so its length may
         * differ from its piece's: the first returns 30 ms less, finish()
         * returns what is left (possibly 0 samples). Concatenated, the
         * results are the whole recording, sample-aligned, nothing lost.
         * A whole-file process() call in between (a preview) ends the stream;
         * the next piece then fails with 'DFN stream interrupted' instead of
         * silently continuing from someone else's state.
         */
        openStream: function(){
            var key = 'dfs' + (_nextStreamKey++);
            var opened = false, done = false, nCh = 0;
            var pend = null;       // dry, 48 kHz, not yet matched with wet: { ch: [..], mono }
            function push(chs, mono){
                if (!pend) { pend = { ch: chs.map(function(a){ return new Float32Array(a); }), mono: mono }; return; }
                pend.ch = pend.ch.map(function(a, i){ return _cat(a, chs[i]); });
                pend.mono = (pend.mono && mono) ? _cat(pend.mono, mono) : null;
            }
            function take(n){
                var out = { ch: [], mono: null };
                if (!pend) { for (var c = 0; c < nCh; c++) out.ch.push(new Float32Array(0)); return out; }
                out.ch = pend.ch.map(function(a){ return a.subarray(0, n); });
                out.mono = pend.mono ? pend.mono.subarray(0, n) : null;
                pend.ch = pend.ch.map(function(a){ return a.subarray(n); });
                pend.mono = pend.mono ? pend.mono.subarray(n) : null;
                return out;
            }
            async function step(audioBuffer, strength, onProgress, close){
                if (!_loaded) throw new Error('DeepFilterNet not initialized');
                if (done) throw new Error('DFN stream already finished');
                var mono;
                if (audioBuffer) {
                    var b48 = await _to48k(audioBuffer);
                    var chs = [];
                    for (var c = 0; c < b48.numberOfChannels; c++) chs.push(b48.getChannelData(c));
                    if (!nCh) nCh = chs.length;
                    else if (chs.length !== nCh) throw new Error('DFN stream: channel count changed');
                    mono = _monoOf(chs);
                    push(chs, nCh > 1 ? new Float32Array(mono) : null);
                } else {
                    mono = new Float32Array(0);
                }
                var wet = await _send(
                    { type: 'process', mono: mono, stream: { key: key, open: !opened, close: !!close } },
                    function(ratio){ if (onProgress) onProgress(0.1 + ratio * 0.75); },
                    [mono.buffer]
                );
                opened = true;
                if (close) done = true;
                var dry = take(wet.length);
                var mixed = nCh ? _blend(wet, dry.ch, nCh > 1 ? dry.mono : null, strength) : { channels: [] };
                if (onProgress) onProgress(1);
                return { channels: mixed.channels, sampleRate: SAMPLE_RATE, numberOfChannels: nCh };
            }
            return {
                process: function(audioBuffer, strength, onProgress){ return step(audioBuffer, strength, onProgress, false); },
                finish: function(strength){ return step(null, strength, null, true); }
            };
        },

        /**
         * Recover from a worker error / state corruption by terminating and
         * re-initializing fresh. Callers (denoise's catch block) use this
         * after a process() failure to retry from a clean state.
         */
        reinit: async function(){
            if (_worker) try { _worker.terminate(); } catch(_){}
            _worker = null;
            _loaded = false;
            _initPromise = null;
            _pending.clear();
            return this.init();
        },

        /**
         * Last init error (null on success / never-init / cleared at start of
         * a fresh init). Read by EngineWarmer wrappers in tool blade files to
         * enrich the `engine_warm_failed` telemetry payload — pre-fix we
         * silently lost the cause every time the worker rejected init.
         */
        get lastError() { return _lastError; },
        get stageGates() { return _stageGates; }
    };
})();
